import React, { ReactElement, useCallback, useState } from 'react';
import { CreativeCredit } from 'sr-types/lib/shortlist/v1/graphql';
import { patchLineItemKeyCredits } from '../../../../shortlists/shortlist';
import { SidePanelDef } from '../../../components/layout/LayoutUtils';
import { SIDE_PANEL_IDS } from '../../../constants/constants';
import I18n from '../../../i18n/I18n';
import { Icons } from '../../../icons/Icons';
import type { KeyCreditsAISidePanelApi, KeyCreditsLineItemRow, UseKeyCreditsAISidePanelOptions } from '../../types';
import KeyCreditsAIPanel from '../components/KeyCreditsAIPanel';

function buildKeyCreditsAIPanelDef(component: (onClose: () => void) => ReactElement | null): SidePanelDef {
    return {
        id: SIDE_PANEL_IDS.keyCreditsAI,
        icon: Icons.AutoAwesomeGradient,
        label: <I18n token="shortlist.line.item.key.credits" />,
        tooltip: <I18n token="shortlist.key.credits.ai.tooltip" />,
        hidden: true,
        component: component as (onClose: () => void) => ReactElement
    };
}

export function useKeyCreditsAISidePanel(options: UseKeyCreditsAISidePanelOptions): KeyCreditsAISidePanelApi {
    const {
        activeOrganizationAccount,
        shortlistIdFallback,
        setLineItems,
        expandView,
        setExpandView,
        onOpen,
        onClosePanel,
        isAIEnabled
    } = options;

    const [keyCreditsPanelLineItem, setKeyCreditsPanelLineItem] = useState<KeyCreditsLineItemRow | undefined>();

    const handleOpenKeyCreditsAI = useCallback(
        (row: KeyCreditsLineItemRow) => {
            if (!row.identity?.id) {
                return;
            }
            setKeyCreditsPanelLineItem(row);
            onOpen?.(row);
        },
        [onOpen]
    );

    const handleCloseKeyCreditsAI = useCallback(() => {
        setKeyCreditsPanelLineItem(undefined);
        onClosePanel?.();
    }, [onClosePanel]);

    const handleKeyCreditsAISaveSuccess = useCallback(
        (lineItemId: string, keyCredits: CreativeCredit[]) => {
            setLineItems((prev) => patchLineItemKeyCredits(prev, lineItemId, keyCredits));
        },
        [setLineItems]
    );

    const renderKeyCreditsAIPanel = useCallback(
        (onClose: () => void) =>
            keyCreditsPanelLineItem ? (
                <KeyCreditsAIPanel
                    lineItem={keyCreditsPanelLineItem}
                    shortlistId={keyCreditsPanelLineItem.resourceShortlistName?.['id'] || shortlistIdFallback}
                    expandView={expandView}
                    setExpandView={setExpandView}
                    activeOrganizationAccount={activeOrganizationAccount}
                    onClose={() => {
                        handleCloseKeyCreditsAI();
                        onClose();
                    }}
                    onSaveSuccess={handleKeyCreditsAISaveSuccess}
                />
            ) : null,
        [
            activeOrganizationAccount,
            expandView,
            handleCloseKeyCreditsAI,
            handleKeyCreditsAISaveSuccess,
            keyCreditsPanelLineItem,
            setExpandView,
            shortlistIdFallback
        ]
    );

    const keyCreditsAIPanelDef = isAIEnabled ? buildKeyCreditsAIPanelDef(renderKeyCreditsAIPanel) : null;

    return {
        keyCreditsPanelLineItem,
        handleOpenKeyCreditsAI,
        handleCloseKeyCreditsAI,
        handleKeyCreditsAISaveSuccess,
        renderKeyCreditsAIPanel,
        keyCreditsAIPanelDef
    };
}
